// Aircraft identity label helpers — display names, palette, glyphs, and
// sort utilities used by the Aircraft detail page and the Map markers.

/**
 * @typedef {'law_enforcement'|'news_media'|'medical'|'fire_sar'|'military'|'gov_other'|'commercial'|'private'|'unknown'} Category
 */

/**
 * @typedef {Object} AircraftLabel
 * @property {Category} category
 * @property {string} [operator]
 * @property {'confirmed'|'inferred'|'guessed'} confidence
 * @property {'allowlist'|'pattern'|'llm'} source
 */

export const CATEGORY_LABELS = Object.freeze({
  law_enforcement: 'Law Enforcement',
  news_media: 'News Media',
  medical: 'Medical',
  fire_sar: 'Fire / SAR',
  military: 'Military',
  gov_other: 'Government — other',
  commercial: 'Commercial',
  private: 'Private',
  unknown: 'Unknown',
});

// OKLCH-based palette mirrored in Map.css. Marker swatches and the legend
// reference these values.
export const CATEGORY_COLORS = Object.freeze({
  military: 'oklch(55% 0.12 110)',
  law_enforcement: 'oklch(50% 0.20 250)',
  medical: 'oklch(55% 0.22 25)',
  fire_sar: 'oklch(70% 0.20 50)',
  news_media: 'oklch(55% 0.22 25)',
  gov_other: 'oklch(50% 0.18 300)',
  commercial: 'oklch(60% 0.12 200)',
  private: 'oklch(60% 0 0)',
  unknown: 'oklch(75% 0 0)',
});

// Inline SVG strings for the marker icons. Hand-rolled simplifications of
// Lucide glyphs to avoid pulling in a runtime icon dependency.
export const CATEGORY_GLYPHS = Object.freeze({
  // shield
  law_enforcement:
    '<svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
  // tv
  news_media:
    '<svg viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="13" rx="2"/><path d="M17 3l-5 4-5-4"/></svg>',
  // cross / plus
  medical:
    '<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>',
  // flame
  fire_sar:
    '<svg viewBox="0 0 24 24"><path d="M12 2c1 4 5 6 5 11a5 5 0 1 1-10 0c0-3 2-4 2-7 2 1 3 0 3-4z"/></svg>',
  // star
  military:
    '<svg viewBox="0 0 24 24"><path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/></svg>',
  // landmark
  gov_other:
    '<svg viewBox="0 0 24 24"><path d="M3 21h18M5 21V10M19 21V10M9 21v-7M15 21v-7M2 9l10-6 10 6"/></svg>',
  // briefcase
  commercial:
    '<svg viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>',
  // user
  private:
    '<svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>',
  // help-circle
  unknown:
    '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4"/><circle cx="12" cy="17" r="0.5" fill="currentColor"/></svg>',
});

// Friendly display labels for `hover_events.event_type` values. The classifier
// emits these five categories; old rows backfilled before migration 0004
// default to 'unclassified'.
export const EVENT_TYPE_LABELS = Object.freeze({
  hover: 'Hover',
  loiter: 'Loiter',
  orbit: 'Orbit',
  traffic_pattern: 'Traffic pattern',
  transit: 'Transit',
  unclassified: 'Unclassified',
});

// Map view colors per event type. Hovers are most concerning (red), orbits
// and loiters next, traffic patterns benign, transit/unclassified de-emphasized.
export const EVENT_TYPE_COLORS = Object.freeze({
  hover: 'oklch(60% 0.22 25)',
  loiter: 'oklch(65% 0.18 40)',
  orbit: 'oklch(70% 0.18 60)',
  traffic_pattern: 'oklch(75% 0.15 90)',
  transit: 'oklch(70% 0 0)',
  unclassified: 'oklch(75% 0 0)',
});

export const SOURCE_LABELS = Object.freeze({
  allowlist: 'curated allowlist',
  pattern: 'rule pattern',
  llm: 'LLM classification',
});

const CONFIDENCE_RANK = Object.freeze({
  confirmed: 3,
  inferred: 2,
  guessed: 1,
});

const CATEGORY_PRIORITY = [
  'military',
  'law_enforcement',
  'medical',
  'fire_sar',
  'news_media',
  'gov_other',
  'commercial',
  'private',
  'unknown',
];

const CATEGORY_PRIORITY_INDEX = Object.freeze(
  CATEGORY_PRIORITY.reduce((acc, name, idx) => {
    acc[name] = idx;
    return acc;
  }, {}),
);

/**
 * Sort labels by confidence desc, then category priority.
 * Pure: returns a new array.
 *
 * @param {AircraftLabel[]} labels
 * @returns {AircraftLabel[]}
 */
export function sortLabels(labels) {
  if (!Array.isArray(labels)) return [];
  return [...labels].sort((a, b) => {
    const ra = CONFIDENCE_RANK[a.confidence] ?? 0;
    const rb = CONFIDENCE_RANK[b.confidence] ?? 0;
    if (ra !== rb) return rb - ra;
    const pa = CATEGORY_PRIORITY_INDEX[a.category] ?? Number.MAX_SAFE_INTEGER;
    const pb = CATEGORY_PRIORITY_INDEX[b.category] ?? Number.MAX_SAFE_INTEGER;
    return pa - pb;
  });
}
